/**
 * Follow API Service — frontend client for /api/follow endpoints.
 *
 * All requests use the shared apiClient (Axios instance) which:
 *   • Sends HttpOnly cookies automatically (withCredentials: true)
 *   • Attaches CSRF tokens on mutating requests
 *   • Handles 401 token-refresh transparently
 */

import { apiClient } from './apiService';

const followService = {
  /**
   * Follow a user.
   * @param {number} userId — Target user's ID
   * @returns {Promise<{ data: { alreadyFollowing: boolean, followerCount: number } }>}
   */
  follow(userId) {
    return apiClient.post(`/follow/${userId}`).then((r) => r.data);
  },

  /**
   * Unfollow a user.
   * @param {number} userId — Target user's ID
   * @returns {Promise<{ data: { wasFollowing: boolean } }>}
   */
  unfollow(userId) {
    return apiClient.delete(`/follow/${userId}`).then((r) => r.data);
  },

  /**
   * Get follower/following counts for a user and whether the current user
   * is already following them.
   * @param {number} userId
   * @returns {Promise<{ data: { followerCount, followingCount, isFollowing } }>}
   */
  getStats(userId) {
    return apiClient.get(`/follow/${userId}/stats`).then((r) => r.data);
  },

  /**
   * Get the current user's own follow stats (used on the dashboard).
   * Equivalent to getStats(currentUserId).
   */
  getMyStats(userId) {
    return followService.getStats(userId);
  },

  /**
   * List users who follow userId (paginated).
   * @param {number} userId
   * @param {{ page?: number, limit?: number }} options
   */
  getFollowers(userId, { page = 1, limit = 20 } = {}) {
    return apiClient
      .get(`/follow/${userId}/followers`, { params: { page, limit } })
      .then((r) => r.data);
  },

  /**
   * List users that userId follows (paginated).
   * @param {number} userId
   * @param {{ page?: number, limit?: number }} options
   */
  getFollowing(userId, { page = 1, limit = 20 } = {}) {
    return apiClient
      .get(`/follow/${userId}/following`, { params: { page, limit } })
      .then((r) => r.data);
  },
};

export default followService;
